import i18next from 'i18next'
import React from 'react'
import { Image, ImageBase, ImageOptions } from '../../../../../utils/image'
import { Link } from '../../../../../utils/link'
import { specialistsBlockClasses } from '../../../../../utils/style'
import { getClassName, htmlDecode } from '../../../../../utils/utils'
import { useInView } from '../../../../hooks/useInView'
import { InviewOptions } from '../../blocksState'

export type SpecialistProps = {
  title?: string
  role?: React.ReactNode
  image: ImageBase
  url: string
}

type SpecialistsBlockLayoutProps = {
  index: number
  department?: string
  title: string
  intro: string
  specialists: SpecialistProps[]
  imageOptions: ImageOptions
  inview?: InviewOptions
}

export type SpecialistsBlockStyle = Partial<{
  blockClassName: string
  inviewClassName: string
  innerClassName: string
  headerClassName: string
  titleClassName: string
  introClassName: string
  contentClassName: string
  listClassName: string
  gridItemClassName: string
  gridArticleClassName: string
  gridWithLinkClassName: string
  gridWithoutLinkClassName: string
  gridContentClassName: string
  gridTitleClassName: string
  gridLabelClassName: string
  gridIntroClassName: string
  gridVisualClassName: string
  gridImageClassName: string
}>

export const SpecialistListLayout = (props: {
  specialists: SpecialistProps[]
  extraChildren?: React.ReactNode
  imageOptions: ImageOptions
}) => {
  const { extraChildren, specialists, imageOptions } = props
  return (
    <ul className={getClassName(specialistsBlockClasses, 'listClassName', '')}>
      {specialists.map((specialist) => (
        <li
          key={`specialist-${specialist.title}`}
          className={getClassName(specialistsBlockClasses, 'gridItemClassName', '')}
        >
          <article className={getClassName(specialistsBlockClasses, 'gridArticleClassName', '')}>
            {specialist.url ? (
              <Link url={specialist.url} className={getClassName(specialistsBlockClasses, 'gridWithLinkClassName', '')}>
                <SpecialistItemContent specialist={specialist} {...props} />
              </Link>
            ) : (
              <div className={getClassName(specialistsBlockClasses, 'gridWithoutLinkClassName', '')}>
                <SpecialistItemContent specialist={specialist} imageOptions={imageOptions} />
              </div>
            )}
          </article>
        </li>
      ))}

      {extraChildren}
    </ul>
  )
}

const SpecialistItemContent = (props: { specialist: SpecialistProps; imageOptions: ImageOptions }) => {
  const { specialist, imageOptions } = props
  return (
    <>
      <div className={getClassName(specialistsBlockClasses, 'gridContentClassName', '')}>
        <span className={getClassName(specialistsBlockClasses, 'gridLabelClassName', '')}>
          {typeof specialist.role === 'string' ? htmlDecode(specialist.role) : specialist.role}
        </span>
        {specialist.title && (
          <h3 className={getClassName(specialistsBlockClasses, 'gridTitleClassName', '')}>{`${specialist.title}`}</h3>
        )}
      </div>

      <div className={getClassName(specialistsBlockClasses, 'gridVisualClassName', '')}>
        <Image
          {...specialist.image}
          {...imageOptions}
          placeholder={specialist.image.urls.get(imageOptions.src) ? specialist.image.urls.get(imageOptions.src) : imageOptions.placeholder}
          classNames={{
            image: getClassName(specialistsBlockClasses, 'gridImageClassName', ''),
          }}
        />
      </div>
    </>
  )
}

export const SpecialistsBlockLayout = (props: SpecialistsBlockLayoutProps) => {
  const { index, department, title, intro, inview, specialists, imageOptions } = props
  const classes = specialistsBlockClasses
  const { inView, ref } = useInView(
    { threshold: 0.1 },
    (inview) => console.debug("SpecialistsBlockLayout inView:", inview),
    !inview?.triggerOnce
  )
  return (
    <section
      key={`block-${index}`}
      id={`io-${index}`}
      ref={ref}
      className={getClassName(classes, 'blockClassName', 'container') + ' ' + (inView ? getClassName(classes, 'inviewClassName', 'inview') : '')}
    >
      <div className={getClassName(classes, 'innerClassName', 'py-5')}>
        <header className={getClassName(classes, 'headerClassName', '')}>
          {title && (
            <h2 className={getClassName(classes, 'titleClassName', 'mb-3')}>
              <span id={`${index}`} aria-hidden="true" className="block__anchor" />
              {title}
            </h2>
          )}
          {intro && <p className={getClassName(classes, 'introClassName', '')}>{intro}</p>}
        </header>

        {specialists.length > 0 && (
          <div className={getClassName(classes, 'contentClassName', '')}>
            <SpecialistListLayout
              specialists={specialists.slice(0, 5)}
              imageOptions={imageOptions}
              extraChildren={
                specialists.length > 5 && (
                  <li className={getClassName(classes, 'gridItemClassName', '')}>
                    <article className={getClassName(classes, 'gridArticleClassName', '')}>
                      <Link
                        url={`/specialisten${department === undefined ? '' : `?filter=${encodeURIComponent(department)}`}`}
                        className={getClassName(classes, 'gridWithLinkClassName', '')}
                      >
                        <div>
                          <span>{`${i18next.t('misc:viewAllSpecialists')}${
                            department === undefined ? '' : `: ${department}`
                          }`}</span>
                        </div>
                      </Link>
                    </article>
                  </li>
                )
              }
            />
          </div>
        )}
      </div>
    </section>
  )
}
